/**
 * Analytics Service for Notification Click-Through Tracking
 * Handles recording and managing email notification engagement data in Firebase
 */

import { apiService } from "../config/api";

export interface NotificationClickTrackingData {
  executionId: string;
  studyId: string;
  researcherId: string;
  clickedAt: string; // ISO timestamp
  participantHighlights: string[]; // List of highlighted participant IDs
  filterApplied?: {
    filter: string;
    sort: string;
  };
  userAgent?: string;
  ipAddress?: string;
}

/**
 * Track notification click-through when alerts tab is opened from email
 * Stores execution_id, timestamp, researcher_id, study_id, and other relevant metadata
 */
export async function trackNotificationClickThrough(
  executionId: string,
  studyId: string,
  participantHighlights: string[],
  filterApplied?: { filter: string; sort: string }
): Promise<void> {
  try {
    const token = await apiService.getAuthToken();
    const base = process.env.REACT_APP_API_URL || "";

    const payload = {
      executionId,
      studyId,
      participantHighlights,
      clickedAt: new Date().toISOString(),
      filterApplied,
    };

    const response = await fetch(
      `${base}/api/anomaly-detection/alerts/track-notification-click`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      // Don't throw - this should not block the user experience
    }
  } catch (error) {
    // Don't throw - this should not block the user experience
  }
}

/**
 * Record that a user dismissed a notification banner
 */
export async function recordNotificationBannerDismissal(
  executionId: string,
  studyId: string
): Promise<void> {
  try {
    const token = await apiService.getAuthToken();
    const base = process.env.REACT_APP_API_URL || "";

    const response = await fetch(
      `${base}/api/anomaly-detection/alerts/dismiss-notification-banner`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          executionId,
          studyId,
          dismissedAt: new Date().toISOString(),
        }),
      }
    );

    if (!response.ok) {
      // Silently fail - this should not block the user experience
    }
  } catch (error) {
    // Silently fail - this should not block the user experience
  }
}
